import { Subscription } from 'rxjs';
import { ColumnType, ListingHelper } from 'src/app/core/utils/listing.helper';

import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { Filter, FilterService } from '@brafton/angular-framework-library';
import { StylesSmartTableComponent } from '@brafton/angular-styles-library';
import { IBaseProject, IPagedList, StatementOfWork } from '@brafton/common-framework-library';

@Component({
  selector: 'shared-sow-listing',
  templateUrl: './sow-listing.component.html',
  styleUrls: ['./sow-listing.component.scss'],
  host: { class: 'sow-listing' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SharedSowListingComponent implements OnInit, OnDestroy, OnChanges {
  @Input() sows!: IPagedList<any>;
  @Input() showColumnHeaders = true;
  @Input() defaultFilters?: Filter[];
  @Input() groupBy?: 'owner' | 'month' | 'status' | 'organization' | 'units';
  @Input() sort!: Filter[];
  @Output() sortChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();
  @HostBinding('class.grouped') get groupingChecker() {
    return this.groupBy !== undefined;
  }

  @Input() get showDate() {
    return (
      this.columnBuilder.isShown('startDate') || this.columnBuilder.isShown('contractualEndDate')
      //|| this.columnBuilder.isShown('workThroughDate')
    );
  }
  set showDate(value: boolean) {
    this.columnBuilder.setShown('startDate', value);
    this.columnBuilder.setShown('contractualEndDate', value);
    //this.columnBuilder.setShown('workThroughDate', value);
  }

  @Input() get showUnits() {
    return this.columnBuilder.isShown('units');
  }
  set showUnits(value: boolean) {
    this.columnBuilder.setShown('units', value);
  }

  @Input() get showOwners() {
    return this.columnBuilder.isShown('owner');
  }
  set showOwners(value: boolean) {
    this.columnBuilder.setShown('owner', value);
  }

  @Input() get showStatus() {
    return this.columnBuilder.isShown('status');
  }
  set showStatus(value: boolean) {
    this.columnBuilder.setShown('status', value);
  }

  @Input() get showOrganization() {
    return this.columnBuilder.isShown('organization');
  }
  set showOrganization(value: boolean) {
    this.columnBuilder.setShown('organization', value);
  }

  @Input() get showActions() {
    return this.columnBuilder.isShown('actions');
  }
  set showActions(value: boolean) {
    this.columnBuilder.setShown('actions', value);
  }

  @ViewChild('smartTable') smartTable!: StylesSmartTableComponent<any>;

  columnBuilder = new ListingHelper();

  // A placeholder for all subscripton so we can unsubscribe all of them in the ondestroy Methode.
  subscriptions = new Subscription();

  constructor(
    private filterService: FilterService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {
    this.columnBuilder.addDefaultColumn('default');
    this.columnBuilder.addColumn('organization', ColumnTypes.Large);
    this.columnBuilder.addColumn('startDate', ColumnTypes.Small);
    this.columnBuilder.addColumn('contractualEndDate', ColumnTypes.Medium);
    //this.columnBuilder.addColumn('workThroughDate', ColumnTypes.Medium);
    this.columnBuilder.addColumn('owner', ColumnTypes.Medium);
    this.columnBuilder.addColumn('status', ColumnTypes.Medium);
    this.columnBuilder.addColumn('units', ColumnTypes.ExtraLarge);
    this.columnBuilder.addColumn('actions', ColumnTypes.VerySmall);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.filterService.registerFilter('sort', this.defaultFilters).subscribe((r: Filter[]) => {
        this.sort = r;
        this.sortChange.emit(this.sort);
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sows']) {
      switch (this.groupBy) {
        case 'owner':
          this.sows.items = this.groupByOwner(this.sows.items as IBaseProject[]);
          break;
        case 'month':
          this.sows.items = this.groupByMonth(this.sows.items as IBaseProject[]);
          break;
        case 'organization':
          this.sows.items = this.groupByFeed(this.sows.items as IBaseProject[]);
          break;
        case 'status':
          this.sows.items = this.groupByStatus(this.sows.items as IBaseProject[]);
          break;
        // case 'units':
        //   this.sows.items = this.groupByUnits(this.sows.items as IBaseProject[]);
        //   break;
        default:
          break;
      }
    }
  }

  getPacePercentage(targetUnits: number, totalUnits: number, completedPercentage: number) {
    if (totalUnits === 0) return 0;
    if (!completedPercentage) completedPercentage = 0;

    const paceBeforeCompleted = targetUnits / totalUnits;
    const paceAfterCompleted = completedPercentage - paceBeforeCompleted;
    const pacePercentageFixed = paceAfterCompleted.toFixed(2);

    return pacePercentageFixed;
  }

  isRolling(sow: StatementOfWork): boolean {
    if (new Date(sow.workThruDate) >= new Date('2099-12-31')) return true;

    return false;
  }

  updateSort(sort: Sort) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { sort: sort.direction === '' ? undefined : sort.active + '-' + sort.direction + ':' + sort.active },
      queryParamsHandling: 'merge',
    });
  }

  buildSort(): Sort | undefined {
    if (this.sort?.length > 0) {
      const active = this.sort[0].value.split('-')[0];
      const direction = this.sort[0].value.split('-')[1];
      return { active: active, direction: direction } as Sort;
    }
    return;
  }

  groupByOwner(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || cur.owner.id !== groupsRemoved[index - 1].owner.id) {
        const y = acc.concat([{ group: cur.owner, field: 'owner' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByMonth(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (
        index === 0 ||
        new Date((cur as any).contractualEndDate).toISOString().substring(0, 7) !==
          new Date((groupsRemoved[index - 1] as any).contractualEndDate).toISOString().substring(0, 7)
      ) {
        const y = acc.concat([{ group: new Date((cur as any).contractualEndDate), field: 'date' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByFeed(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || (cur as any).feedDetails.feedId !== (groupsRemoved[index - 1] as any).feedDetails.feedId) {
        const y = acc.concat([{ group: (cur as any).feedDetails, field: 'organization' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  groupByStatus(items: IBaseProject[]): IBaseProject[] | Group[] {
    const groupsRemoved = items.filter((i: any) => !i.group);
    const results = groupsRemoved.reduce((acc: any[], cur: IBaseProject, index: number) => {
      if (index === 0 || cur.status.id !== groupsRemoved[index - 1].status.id) {
        const y = acc.concat([{ group: cur.status, field: 'status' } as Group, cur]);
        return y;
      } else {
        const z = acc.concat(cur);
        return z;
      }
    }, []);
    return results;
  }

  // groupByUnits(items: any): IBaseProject[] | Group[] {
  //   const groupsRemoved = items.filter((i: any) => !i.group);
  //   const results = groupsRemoved.reduce((acc: any[], cur: any, index: number) => {
  //     if (index === 0 || cur.units !== groupsRemoved[index - 1].units) {
  //       const y = acc.concat([{ group: cur.units, field: 'units' } as Group, cur]);
  //       return y;
  //     } else {
  //       const z = acc.concat(cur);
  //       return z;
  //     }
  //   }, []);
  //   return results;
  // }
}

class ColumnTypes {
  static readonly VerySmall: ColumnType = new ColumnType(2, 3);
  static readonly Small: ColumnType = new ColumnType(9, 12);
  static readonly Medium: ColumnType = new ColumnType(10, 20);
  static readonly Large: ColumnType = new ColumnType(12, 25);
  static readonly ExtraLarge: ColumnType = new ColumnType(25, 30);
}

interface Group {
  group: any;
  field: string;
}
